import {
  ApolloClient,
  ApolloLink,
  InMemoryCache,
  NormalizedCacheObject,
  split,
} from "@apollo/client";
import {
  getMainDefinition,
  offsetLimitPagination,
} from "@apollo/client/utilities";
import { useEffect, useState } from "react";

import { authLink } from "../config/graphql/authLink";
import { errorLink } from "../config/graphql/errorLink";
import { createSseLink } from "../config/graphql/sseLink";
import { createFileUploadLink } from "../config/graphql/uploadLink";
import { useUserContext } from "../contexts/UserContext";
import { UserActionTypes } from "../contexts/reducers/userReducer";
import { RefreshAccessMutation } from "../graphql/deliveryService/mutations/refreshAccess";
import { RefreshAccessFragment } from "../models/autogenerated-deliveryService-gql/graphql";
import { graphqlUnionTypes } from "../utils/graphqlHelper";

// documentation for apollo graphql
// https://www.apollographql.com/docs/react

//Using this logic, queries and mutations will use HTTP (httpLink) as normal, and subscriptions will use Server Sent Events (sseLink).
const splitLink = (token: string | null) => {
  return split(
    ({ query }) => {
      const definition = getMainDefinition(query);
      return (
        definition.kind === "OperationDefinition" &&
        definition.operation === "subscription"
      );
    },
    createSseLink(token),
    createFileUploadLink(),
  );
};

const createApolloClient = (
  token: string | null,
  refreshAccess: () => Promise<RefreshAccessFragment | undefined>,
) => {
  return new ApolloClient({
    link: ApolloLink.from([...errorLink(refreshAccess), splitLink(token)]),
    cache: new InMemoryCache({
      //declare your Union Types here in order for ApolloClient to know how to handle them
      possibleTypes: graphqlUnionTypes,
      typePolicies: {
        //these fields specifify the queries which use pagination and therefore should be treated differently in cache
        InboundDeliveryOverviewPaged: {
          fields: {
            data: offsetLimitPagination(["sort"]),
          },
        },
      },
    }),
  });
};

export const useApolloClient = () => {
  const { user, dispatchUser } = useUserContext();
  const { accessToken, refreshToken } = user;

  const refreshAccess = async () => {
    const response = await client.mutate({
      mutation: RefreshAccessMutation,
      variables: {
        refreshAccessInput: {
          accessToken: accessToken || "",
          refreshToken: refreshToken || "",
        },
      },
    });

    if (
      (response.errors && response.errors.length > 0) ||
      !response.data?.refresh.refreshOutput
    ) {
      dispatchUser({ type: UserActionTypes.Logout });
      return;
    }

    dispatchUser({
      type: UserActionTypes.RefreshAccess,
      value: response.data.refresh.refreshOutput,
    });

    return response.data.refresh.refreshOutput;
  };

  //this needs to stay a state, otherwise it will be recreated on every render
  const [client] = useState<ApolloClient<NormalizedCacheObject>>(
    createApolloClient(accessToken, refreshAccess),
  );

  const dispatchEvent = () => {
    const event = new Event("logInSuccessful");
    window.dispatchEvent(event);
  };

  const updateClientWithAuthorization = () => {
    client.setLink(
      ApolloLink.from([
        authLink(accessToken),
        ...errorLink(refreshAccess),
        splitLink(accessToken),
      ]),
    );
  };

  //once the user logs in, we need to update the client to append the token to the request headers
  useEffect(() => {
    if (!accessToken) return;

    updateClientWithAuthorization();
    dispatchEvent();
  }, [accessToken]);

  return client;
};
